import { QueryClient } from '@tanstack/react-query';
import { reportClientError } from './client-telemetry';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Longer cache time for better performance (15 minutes)
      staleTime: 15 * 60 * 1000,
      // Keep data in cache longer (30 minutes)
      gcTime: 30 * 60 * 1000,
      // Retry failed requests
      retry: (failureCount, error: Error & { status?: number }) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status !== undefined && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry up to 2 times for faster response
        return failureCount < 2;
      },
      // Faster retry delay
      retryDelay: attemptIndex => Math.min(500 * 2 ** attemptIndex, 15000),
      // Disable refetch on window focus for better performance
      refetchOnWindowFocus: false,
      // Avoid extra reloads when the tab regains network
      refetchOnReconnect: false,
      // Enable background updates without forced refetch on every mount
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Global error handler for mutations
      onError: error => {
        reportClientError('Mutation error', error);
        // Here you could show a global toast notification
        // toast.error(error?.message || 'Something went wrong');
      },
    },
  },
});

// Query Keys Factory - Centralized query key management with organization context
export const queryKeys = {
  // Contacts
  contacts: {
    all: (orgSlug?: string) => ['contacts', orgSlug] as const,
    lists: (orgSlug?: string) => [...queryKeys.contacts.all(orgSlug), 'list'] as const,
    list: <T extends object>(orgSlug: string | undefined, params: T) =>
      [...queryKeys.contacts.lists(orgSlug), params] as const,
    details: (orgSlug?: string) => [...queryKeys.contacts.all(orgSlug), 'detail'] as const,
    detail: (orgSlug: string | undefined, id: string) =>
      [...queryKeys.contacts.details(orgSlug), id] as const,
  },

  // Companies
  companies: {
    all: (orgSlug?: string) => ['companies', orgSlug] as const,
    lists: (orgSlug?: string) => [...queryKeys.companies.all(orgSlug), 'list'] as const,
    list: <T extends object>(orgSlug: string | undefined, params: T) =>
      [...queryKeys.companies.lists(orgSlug), params] as const,
    details: (orgSlug?: string) => [...queryKeys.companies.all(orgSlug), 'detail'] as const,
    detail: (orgSlug: string | undefined, id: string) =>
      [...queryKeys.companies.details(orgSlug), id] as const,
  },

  // Notes
  notes: {
    all: (orgSlug?: string) => ['notes', orgSlug] as const,
    byContact: (orgSlug: string | undefined, contactId: string) =>
      [...queryKeys.notes.all(orgSlug), 'contact', contactId] as const,
  },

  // Attachments
  attachments: {
    all: (orgSlug?: string) => ['attachments', orgSlug] as const,
    byContact: (orgSlug: string | undefined, contactId: string) =>
      [...queryKeys.attachments.all(orgSlug), 'contact', contactId] as const,
  },

  // Pipeline
  pipeline: {
    all: (orgSlug?: string) => ['pipeline', orgSlug] as const,
    list: (
      orgSlug: string | undefined,
      apiUrl: string,
      principalId: string | undefined,
      organizationId: string | undefined,
      workspaceId: string | null
    ) =>
      [
        ...queryKeys.pipeline.all(orgSlug),
        'list',
        apiUrl,
        principalId,
        organizationId,
        workspaceId,
      ] as const,
    boards: (orgSlug?: string) => [...queryKeys.pipeline.all(orgSlug), 'board'] as const,
    board: (
      orgSlug: string | undefined,
      pipelineId: string,
      page?: number | string,
      limit?: number
    ) => [...queryKeys.pipeline.boards(orgSlug), pipelineId, page, limit] as const,
    history: (orgSlug: string | undefined, contactId: string) =>
      [...queryKeys.pipeline.all(orgSlug), 'history', contactId] as const,
  },

  settings: {
    all: (orgSlug?: string) => ['settings', orgSlug] as const,
    org: (orgSlug?: string) => [...queryKeys.settings.all(orgSlug), 'org'] as const,
  },

  packs: {
    all: (orgSlug?: string) => ['packs', orgSlug] as const,
  },

  customObjects: {
    all: (orgSlug?: string) => ['custom-objects', orgSlug] as const,
    records: (orgSlug: string | undefined, objectKey: string) =>
      ['object-records', orgSlug, objectKey] as const,
    record: (orgSlug: string | undefined, objectKey: string, id: string) =>
      ['object-records', orgSlug, objectKey, 'record', id] as const,
  },

  customFields: {
    all: (orgSlug?: string) => ['custom-fields', orgSlug] as const,
    list: (orgSlug: string | undefined, objectType: string) =>
      [...queryKeys.customFields.all(orgSlug), objectType] as const,
  },
} as const;
